// Parallelogram shape strategy — top edge shifted right by 25% of width

import { registerShape } from '../services/ShapeRegistry';
import { findEdgeSideIntersection } from '../utils/geometryUtils';
import type { LineSeg } from '../utils/geometryUtils';
import type {
  ShapeDefinition,
  ShapeDrawSettings,
  EdgeData,
  Point,
  CollisionShape,
} from '../types/ShapeTypes';

const SKEW_RATIO = 0.25;

function getVertices(cx: number, cy: number, w: number, h: number): [Point, Point, Point, Point] {
  const skew = w * SKEW_RATIO;
  return [
    { x: cx - w / 2 + skew, y: cy - h / 2 },   // top-left
    { x: cx + w / 2, y: cy - h / 2 },            // top-right
    { x: cx + w / 2 - skew, y: cy + h / 2 },    // bottom-right
    { x: cx - w / 2, y: cy + h / 2 },            // bottom-left
  ];
}

function getParallelogramSides(vertices: [Point, Point, Point, Point]): LineSeg[] {
  const [v0, v1, v2, v3] = vertices;
  return [
    { x3: v0.x, y3: v0.y, x4: v1.x, y4: v1.y },
    { x3: v1.x, y3: v1.y, x4: v2.x, y4: v2.y },
    { x3: v2.x, y3: v2.y, x4: v3.x, y4: v3.y },
    { x3: v3.x, y3: v3.y, x4: v0.x, y4: v0.y },
  ];
}

function drawPath(settings: ShapeDrawSettings): void {
  const { ctx, node, center: { x, y }, border, zoomLevel } = settings;
  const width = border ? node.width + node.borderWidth : node.width + node.borderWidth * 2;
  const height = border ? node.height + node.borderWidth : node.height + node.borderWidth * 2;
  const computedWidth = node.border ? width : node.width;
  const computedHeight = node.border ? height : node.height;

  const vertices = getVertices(x, y, computedWidth * zoomLevel, computedHeight * zoomLevel);
  ctx.moveTo(vertices[0].x, vertices[0].y);
  ctx.lineTo(vertices[1].x, vertices[1].y);
  ctx.lineTo(vertices[2].x, vertices[2].y);
  ctx.lineTo(vertices[3].x, vertices[3].y);
  ctx.closePath();
}

function getEdgeConnectionPoint(edge: EdgeData, connected: 'from' | 'to'): Point {
  const line1 = { x1: edge.startPosX, y1: edge.startPosY, x2: edge.endPosX, y2: edge.endPosY };

  const isFrom = connected === 'from';
  const nodeWidth = isFrom
    ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
  const nodeHeight = isFrom
    ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;
  const cx = isFrom ? edge.startPosX : edge.endPosX;
  const cy = isFrom ? edge.startPosY : edge.endPosY;

  const vertices = getVertices(cx, cy, nodeWidth, nodeHeight);
  const sides = getParallelogramSides(vertices);
  return findEdgeSideIntersection(line1, sides);
}

function getCollisionData(edge: EdgeData, side: 'from' | 'to'): CollisionShape {
  const isFrom = side === 'from';
  const x = isFrom
    ? edge.startPosX - edge.fromNodeWidth / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosX - edge.toNodeWidth / 2 - edge.toNodeBorderWidthValue;
  const y = isFrom
    ? edge.startPosY - edge.fromNodeHeight / 2 - edge.fromNodeBorderWidthValue
    : edge.endPosY - edge.toNodeHeight / 2 - edge.toNodeBorderWidthValue;
  const width = isFrom
    ? edge.fromNodeWidth + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeWidth + edge.toNodeBorderWidthValue * 2;
  const height = isFrom
    ? edge.fromNodeHeight + edge.fromNodeBorderWidthValue * 2
    : edge.toNodeHeight + edge.toNodeBorderWidthValue * 2;

  return { type: 'rect', data: { x, y, width, height } };
}

const parallelogramShape: ShapeDefinition = {
  value: 'parallelogram',
  drawPath,
  getEdgeConnectionPoint,
  getCollisionData,
  resize: { constrainDimensions: false },
};

registerShape(parallelogramShape);

export { parallelogramShape };
